import { useState, useRef } from 'react';
import type { ReactNode } from 'react';
import Pill from '../components/Pill';
import ResponseCard from '../components/ResponseCard';
import ModeToggle from '../components/ModeToggle';
import { generateReplies } from '../lib/api';
import type { GeneratedReply, RefineStyle } from '../lib/api';

const PLATFORMS = ['Reddit', 'X', 'Instagram', 'TikTok', 'LinkedIn', 'Other'];

const PLATFORM_PLACEHOLDERS: Record<string, string> = {
  Reddit:
    'Honestly your post just shows you have no idea what you\'re talking about. Maybe try learning the basics before sharing your "thoughts" with the rest of us.',
  X:
    "this is the most embarrassing take i've seen all week. delete and rethink everything",
  Instagram: 'lol no one asked',
  TikTok: 'no shot you actually believe this 💀',
  LinkedIn:
    "I respectfully disagree. This take demonstrates a fundamental misunderstanding of the industry. I'd recommend reading more before posting opinions like this.",
  Other: 'Paste the comment you want to respond to...',
};

const SAMPLE_REPLIES: GeneratedReply[] = [
  {
    style: 'final',
    type: 'THE FINAL WORD',
    text: "We disagree. I've made my position clear and I'm not interested in rephrasing it for you. You can take it or leave it.",
  },
  {
    style: 'burn',
    type: 'THE BURN',
    text: "Funny — you've written more words about how unimportant my post is than the post itself. Almost like it bothered you on purpose.",
  },
  {
    style: 'receipts',
    type: 'THE RECEIPTS',
    text: "The claim falls apart on inspection. You've offered no evidence and asserted the opposite of what most published research on the topic shows. Happy to hear a source if you have one.",
  },
  {
    style: 'nuke',
    type: 'THE NUKE',
    text: "You typed all of that out and hit post. Genuinely embarrassing for you. Touch grass and reconsider whatever your day looks like that this is how you spend it.",
  },
];

const REFINE_OPTIONS: { key: RefineStyle; label: string }[] = [
  { key: 'shorter', label: 'Shorter' },
  { key: 'sharper', label: 'Sharper' },
  { key: 'calmer', label: 'Calmer' },
];

const RECEIPTS_CAVEAT =
  'Verify any specific facts, stats, or sources before posting — the model can sometimes confidently state things that are wrong.';

export default function HavingIt() {
  const [platform, setPlatform] = useState('Reddit');
  const [comment, setComment] = useState('');
  const [replies, setReplies] = useState<GeneratedReply[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resultsRef = useRef<HTMLDivElement>(null);

  const generate = async () => {
    if (!comment.trim()) return;
    setLoading(true);
    setError(null);
    setReplies(null);
    try {
      const result = await generateReplies(platform, comment);
      setReplies(result);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }, 80);
    } catch (e) {
      setError(
        e instanceof Error && e.message
          ? e.message
          : 'Could not generate right now. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const display = replies ?? (loading ? null : SAMPLE_REPLIES);

  return (
    <div>
      <section className="pad-x" style={{ padding: '64px 40px 60px', maxWidth: 780 }}>
        <ModeToggle />
        <div
          style={{
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.14em',
            color: 'var(--tag)',
            marginBottom: 24,
            textTransform: 'uppercase',
          }}
        >
          Cutting. Composed. Or scorched-earth.
        </div>
        <h1
          className="hero-title"
          style={{
            fontSize: 52,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
          }}
        >
          Comebacks for things
        </h1>
        <h1
          className="hero-title"
          style={{
            fontSize: 52,
            fontWeight: 700,
            lineHeight: 1.1,
            letterSpacing: '-0.03em',
            color: '#4a4744',
            marginBottom: 28,
          }}
        >
          you wish you hadn't read.
        </h1>
        <p
          style={{
            fontSize: 15,
            lineHeight: 1.65,
            color: 'var(--fg-muted)',
            maxWidth: 440,
            textWrap: 'pretty',
          }}
        >
          Paste the comment. Pick the platform. Get four replies — composed,
          witty, fact-led, or scorched-earth. Pick the one that fits your
          mood; refine if it isn't quite right yet.
        </p>
      </section>

      <div style={{ height: 1, background: 'var(--border)' }} />

      <section
        className="pad-x two-col"
        style={{
          padding: '48px 40px 80px',
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 48,
          maxWidth: 1100,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
          <div>
            <Label>Where is the comment?</Label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 }}>
              {PLATFORMS.map((p) => (
                <Pill
                  key={p}
                  label={p}
                  active={platform === p}
                  onClick={() => setPlatform(p)}
                />
              ))}
            </div>
          </div>

          <div>
            <Label>The comment you want to respond to</Label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={PLATFORM_PLACEHOLDERS[platform]}
              maxLength={1500}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) generate();
              }}
              onFocus={(e) => (e.currentTarget.style.borderColor = '#555')}
              onBlur={(e) => (e.currentTarget.style.borderColor = 'var(--border)')}
              style={{
                width: '100%',
                minHeight: 150,
                padding: '14px 16px',
                background: 'var(--input-bg)',
                border: '1px solid var(--border)',
                borderRadius: 6,
                color: 'var(--fg)',
                fontFamily: 'inherit',
                fontSize: 14,
                lineHeight: 1.6,
                resize: 'vertical',
                transition: 'border-color 0.15s',
              }}
            />
            <div style={{ fontSize: 11, color: 'var(--fg-dim)', marginTop: 6 }}>
              {comment.length}/1500 · ⌘↵ to generate
            </div>
          </div>

          <button
            type="button"
            onClick={generate}
            disabled={loading || !comment.trim()}
            style={{
              width: '100%',
              padding: '16px 24px',
              background: loading || !comment.trim() ? '#2a2828' : 'var(--accent)',
              color: loading || !comment.trim() ? '#555' : '#0f0f0f',
              border: 'none',
              borderRadius: 6,
              fontFamily: 'inherit',
              fontSize: 15,
              fontWeight: 600,
              cursor: loading || !comment.trim() ? 'default' : 'pointer',
              transition: 'background 0.2s',
              letterSpacing: '-0.01em',
            }}
          >
            {loading ? 'Drafting your replies…' : 'Generate my replies →'}
          </button>

          {error && <Notice tone="err">{error}</Notice>}
        </div>

        <div ref={resultsRef} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {loading
            ? [0, 1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="skeleton"
                  style={{
                    background: 'var(--card-bg)',
                    border: '1px solid var(--border)',
                    borderRadius: 8,
                    padding: '22px 24px',
                    height: 140,
                    animationDelay: `${i * 0.12}s`,
                  }}
                />
              ))
            : display?.map((reply, i) => (
                <ResponseCard
                  key={`${reply.type}-${i}`}
                  item={reply}
                  refineOptions={REFINE_OPTIONS}
                  caveat={reply.style === 'receipts' ? RECEIPTS_CAVEAT : undefined}
                />
              ))}
        </div>
      </section>

      <footer
        className="pad-x"
        style={{
          borderTop: '1px solid var(--border)',
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 20,
          flexWrap: 'wrap',
        }}
      >
        <span style={{ fontSize: 13, color: 'var(--tag)' }}>
          Hard limits: no slurs, no threats, no doxxing. Cutting is fine; cruel
          isn't.
        </span>
        <span style={{ fontSize: 13, color: 'var(--tag)', fontStyle: 'italic' }}>
          "Touch grass — but with style."
        </span>
      </footer>
    </div>
  );
}

function Label({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.12em',
        color: 'var(--tag)',
        textTransform: 'uppercase',
        marginBottom: 14,
      }}
    >
      {children}
    </div>
  );
}

function Notice({ children }: { tone: 'err'; children: ReactNode }) {
  return (
    <div
      style={{
        background: '#2a1a1a',
        border: '1px solid #5c2a2a',
        color: '#e29090',
        padding: '10px 14px',
        borderRadius: 6,
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}
